import type { LexicalNode } from "lexical";
import { $isListItemNode, $isListNode } from "@lexical/list";

/**
 * Lexical 用 Tab 缩进时，会在前一项后面插入一个「仅含嵌套 ListNode」的 li 作为壳，
 * 被缩进的行并不作为前一项的子节点，而是该壳的子节点（与 HTML 里「整段嵌在父 li 内」不同）。
 * 此类 li 的首子节点即为 ListNode。
 */
export function isListItemIndentWrapper(node: LexicalNode | null | undefined): boolean {
  if (!$isListItemNode(node)) return false;
  const first = node.getFirstChild();
  return first !== null && $isListNode(first);
}

/** 收集某列表项及其所有嵌套子列表项的 key（含自身）——仅覆盖「真·子树」内的 li */
export function $collectListItemSubtreeKeys(item: LexicalNode, out: Set<string>): void {
  if (!$isListItemNode(item)) return;
  out.add(item.getKey());
  for (const ch of item.getChildren()) {
    if ($isListNode(ch)) {
      for (const sub of ch.getChildren()) {
        $collectListItemSubtreeKeys(sub, out);
      }
    }
  }
}

/**
 * 展开「逻辑块」：当前 li + 其内部嵌套列表中的全部 li，
 * 再加上 Lexical 缩进产生的、紧随其后的连续「缩进包装 li」链及其子树。
 */
export function $expandListItemDragBlock(start: LexicalNode): {
  moveChain: LexicalNode[];
  surfaceKeys: Set<string>;
} {
  const surface = new Set<string>();
  if (!$isListItemNode(start)) {
    surface.add(start.getKey());
    return { moveChain: [start], surfaceKeys: surface };
  }

  const chain: LexicalNode[] = [start];
  let sib: LexicalNode | null = start.getNextSibling();
  while (sib !== null && $isListItemNode(sib) && isListItemIndentWrapper(sib)) {
    chain.push(sib);
    sib = sib.getNextSibling();
  }

  for (const li of chain) {
    $collectListItemSubtreeKeys(li, surface);
  }
  return { moveChain: chain, surfaceKeys: surface };
}

export type ListDragExpansionBlock = {
  /** 同一父 ListNode 下需按文档顺序整体移动的 li（含缩进壳） */
  moveRootKeys: string[];
  /** 高亮、落点排除、幽灵图联合矩形 */
  surfaceKeys: string[];
};

/**
 * 多选根 key（已 filter）→ 合并 Lexical 缩进包装链；每块独立一组 move/surface。
 */
export function buildListDragExpansionFromRoots(
  filteredRootKeys: string[],
  $getNodeByKey: (k: string) => LexicalNode | null,
  documentOrderIndex: Map<string, number>,
): { blocks: ListDragExpansionBlock[]; surfaceKeys: string[]; moveRootKeys: string[] } {
  const canonicalRoots: string[] = [];
  for (const rk of filteredRootKeys) {
    const n = $getNodeByKey(rk);
    if ($isListItemNode(n) && isListItemIndentWrapper(n)) {
      const prev = n.getPreviousSibling();
      // Lexical 缩进壳被误选中时，提升为前一个真实条目，保证“自身+后代”一起移动
      if ($isListItemNode(prev) && !isListItemIndentWrapper(prev)) {
        canonicalRoots.push(prev.getKey());
        continue;
      }
    }
    canonicalRoots.push(rk);
  }

  const sortedRoots = [...new Set(canonicalRoots)].sort(
    (a, b) => (documentOrderIndex.get(a) ?? 0) - (documentOrderIndex.get(b) ?? 0),
  );

  const blocks: ListDragExpansionBlock[] = [];
  const handled = new Set<string>();

  for (const rk of sortedRoots) {
    if (handled.has(rk)) continue;
    const node = $getNodeByKey(rk);
    if (!node) continue;

    if (!$isListItemNode(node)) {
      handled.add(rk);
      blocks.push({ moveRootKeys: [rk], surfaceKeys: [rk] });
      continue;
    }

    const { moveChain, surfaceKeys: sub } = $expandListItemDragBlock(node);
    for (const k of sub) handled.add(k);
    blocks.push({
      moveRootKeys: moveChain.map((li) => li.getKey()),
      surfaceKeys: [...sub],
    });
  }

  const surfaceMerged = [...new Set(blocks.flatMap((b) => b.surfaceKeys))];
  const moveMerged = blocks.flatMap((b) => b.moveRootKeys);

  return { blocks, surfaceKeys: surfaceMerged, moveRootKeys: moveMerged };
}

/**
 * 多选时：若同时选中了父 li 与子 li，只保留「根」——避免子项被单独再移动一次。
 */
export function filterListDragRootKeys(
  keys: string[],
  $getNodeByKey: (k: string) => LexicalNode | null,
): string[] {
  const set = new Set(keys);
  return keys.filter((k) => {
    const node = $getNodeByKey(k);
    if (!$isListItemNode(node)) return true;
    let p: LexicalNode | null = node.getParent();
    while (p) {
      if ($isListItemNode(p) && set.has(p.getKey())) return false;
      p = p.getParent();
    }
    return true;
  });
}
